import React, { useMemo } from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { ChartSimpleIcon } from '@revfluence/fresh-icons/solid/esm';
import { Card } from '@frontend/shadcn/components/ui/card';
import { Empty } from '@frontend/app/refresh-components';
import { useFragment, type FragmentType } from '@frontend/gql/social';
import { graphql } from '@frontend/gql/social';
import { format, parseISO } from 'date-fns';
import { formatNumberDefault } from '../../../utils/formatNumber';
import { GraphCardHeader, GraphCardTitle, GraphCardContent } from '../../../components/graph-card';
import MetricChangeIndicator from '../../../components/metric-change-indicator';
import { DateType } from '../../../types';

export const followerGrowthCardFragment = graphql(`
  fragment FollowerGrowthCard_TotalInsights on TotalInsights {
    followerGrowth
  }
`);

interface FollowerGrowthCardProps {
  className?: string;
  startDate?: string;
  endDate?: string;
  dateType?: DateType;
  data: FragmentType<typeof followerGrowthCardFragment> | null | undefined;
  previousPeriodData?: FragmentType<typeof followerGrowthCardFragment> | null | undefined;
  timeSeriesData?: {
    accountFollowersBreakdown?: {
      daily?: {
        followersByDate?: Array<{
          date: string;
          count: number;
        }>;
      };
    };
  } | null;
}

interface FollowerGrowthData {
  date: string;
  followers: number;
  formattedDate: string;
}

// -----------------------------------------------------------------------------
// Helper — label formatter for Recharts Tooltip (kept outside component to avoid
// re-creation on every render and to provide strong typing)
// -----------------------------------------------------------------------------

interface TooltipPayloadItem {
  payload?: {
    date?: string;
  };
}

const followerGrowthLabelFormatter = (
  label: string | number,
  payload: TooltipPayloadItem[]
): string => {
  const isoDate = payload?.[0]?.payload?.date;
  try {
    return isoDate ? `Date: ${format(parseISO(isoDate), 'MMM d, yyyy')}` : String(label);
  } catch {
    return String(label);
  }
};
// -----------------------------------------------------------------------------

const FollowerGrowthCard = React.forwardRef<HTMLDivElement, FollowerGrowthCardProps>(
  ({ className, startDate, endDate, dateType, data, previousPeriodData, timeSeriesData }, ref) => {
    // Extract follower growth data using the fragment
    const totalInsights = useFragment(followerGrowthCardFragment, data);
    const previousTotalInsights = useFragment(followerGrowthCardFragment, previousPeriodData);

    // Get current and previous period follower growth
    const currentFollowerGrowth = totalInsights?.followerGrowth ?? 0;
    const previousFollowerGrowth = previousTotalInsights?.followerGrowth ?? 0;

    // Transform time series data into chart format
    const chartData = useMemo(() => {
      if (!timeSeriesData?.accountFollowersBreakdown?.daily?.followersByDate?.length) return [];

      // Sort data by date in descending order to keep the latest snapshot per date
      const validData = timeSeriesData.accountFollowersBreakdown.daily.followersByDate
        .filter((item): item is { date: string; count: number } => {
          try {
            return (
              typeof item.date === 'string' &&
              typeof item.count === 'number' &&
              !isNaN(item.count) &&
              !isNaN(parseISO(item.date).getTime())
            );
          } catch {
            return false;
          }
        })
        .sort((a, b) => parseISO(b.date).getTime() - parseISO(a.date).getTime());

      // Use Map to deduplicate by date, keeping the latest snapshot (first occurrence after descending sort)
      const uniqueData = new Map();
      validData.forEach((item) => {
        // Only set if the date doesn't exist yet, preserving the first (latest) occurrence
        if (!uniqueData.has(item.date)) {
          uniqueData.set(item.date, item.count);
        }
      });

      return Array.from(uniqueData.entries()).map(([date, count]): FollowerGrowthData => ({
        date, // Keep full ISO date for internal use
        followers: count,
        formattedDate: format(parseISO(date), 'MMM d'), // Format only for display
      }));
    }, [timeSeriesData]);

    // Determine if we have real data
    const hasRealData = chartData.length > 0;

    // Calculate peak followers for screen reader summary
    const peakFollowers = useMemo(() => {
      if (!hasRealData) return 0;
      return Math.max(...chartData.map((data) => data.followers));
    }, [chartData, hasRealData]);

    return (
      <Card ref={ref} className={className} role="region" aria-label="Follower growth over time">
        <GraphCardHeader className="border-b">
          <GraphCardTitle>Follower Growth</GraphCardTitle>
        </GraphCardHeader>
        <GraphCardContent className="pt-4 flex flex-col justify-between flex-1">
          <div className="mb-4 ml-12">
            <div
              className="font-sans text-[32px] font-medium text-gray-900 leading-[48px] mb-1"
              aria-live="polite"
              aria-atomic="true"
            >
              {formatNumberDefault(currentFollowerGrowth)}
            </div>
            <MetricChangeIndicator
              current={currentFollowerGrowth}
              previous={previousFollowerGrowth}
              startDate={startDate}
              endDate={endDate}
              dateType={dateType}
            />
            {!hasRealData && (
              <div className="text-xs text-muted-foreground mt-2 italic">
                ⚠️ No follower growth data available for this period.
              </div>
            )}
          </div>

          <div
            className="sm:h-[200px] h-[220px]"
            role="graphics-document"
            aria-label={`Line chart showing follower growth over time. Total growth: ${formatNumberDefault(currentFollowerGrowth)}`}
          >
            <ResponsiveContainer width="100%" height="100%">
              {!hasRealData ? (
                <Empty
                  icon={ChartSimpleIcon}
                  title="No follower data available."
                  description="Follower growth data will appear here once available."
                  size="lg"
                />
              ) : (
                <LineChart data={chartData} aria-hidden="true" margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="formattedDate"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={10}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tickMargin={10}
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value: number) => String(formatNumberDefault(value))}
                  />
                  <Tooltip
                    labelStyle={{ color: '#374151' }}
                    formatter={(value: number) => [formatNumberDefault(value), 'Followers']}
                    labelFormatter={followerGrowthLabelFormatter}
                  />
                  <Line
                    type="linear"
                    dataKey="followers"
                    stroke="#4F7DBA"
                    strokeWidth={4}
                    dot={false}
                    activeDot={{ r: 6, stroke: '#4F7DBA', strokeWidth: 2 }}
                    role="graphics-symbol"
                    aria-label="Follower growth trend line"
                  />
                </LineChart>
              )}
            </ResponsiveContainer>
          </div>

          {/* Screen reader accessible table */}
          <div className="sr-only">
            <table>
              <caption>Follower growth by time period</caption>
              <thead>
                <tr>
                  <th scope="col">Date</th>
                  <th scope="col">Followers</th>
                </tr>
              </thead>
              <tbody>
                {chartData.map((data) => (
                  <tr key={data.date}>
                    <th scope="row">{data.formattedDate}</th>
                    <td>{formatNumberDefault(data.followers)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Summary for screen readers */}
          <div className="sr-only" aria-live="polite">
            <h3>Follower Growth Summary:</h3>
            <ul>
              <li>Total follower growth: {formatNumberDefault(currentFollowerGrowth)}</li>
              {hasRealData && (
                <li>
                  Current period: {currentFollowerGrowth}, Previous period: {previousFollowerGrowth}
                </li>
              )}
              {hasRealData && (
                <li>
                  Peak followers: {formatNumberDefault(peakFollowers)}
                </li>
              )}
            </ul>
          </div>
        </GraphCardContent>
      </Card>
    );
  },
);

FollowerGrowthCard.displayName = 'FollowerGrowthCard';

export default FollowerGrowthCard;
